import type { DiagnosticLogEntry } from "@/context/platform"

export type ErrorReportChannel = "dev" | "beta" | "prod" | undefined

export type ErrorReportPayload = {
  source: "openanalyst-desktop"
  kind: "error-page"
  email?: string
  message?: string
  app: {
    version?: string
    channel?: string
    platform?: string
    os?: string
  }
  error: {
    name?: string
    message?: string
    stack: string
  }
  diagnostics: {
    entries: string[]
    truncated: boolean
    unavailable?: boolean
  }
  sentryEventId?: string
  reportFingerprint?: string
}

type ErrorReportFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export function defaultErrorReportUrl(channel: ErrorReportChannel) {
  return channel === "prod" ? "https://error.openanalyst.com/v1/report" : "https://error.basera.com/v1/report"
}

export function formatDiagnosticEntryForReport(entry: DiagnosticLogEntry) {
  const fields = entry.fields.length ? ` ${entry.fields.map((field) => `${field.key}=${field.value}`).join(" ")}` : ""
  return `[${entry.timestamp}] ${entry.level} ${entry.source}/${entry.target} ${entry.tags.join(",")} ${entry.message}${fields}`
}

function parseErrorText(errorText: string) {
  const first = errorText.split("\n")[0] ?? ""
  const match = first.match(/^([^:]+):\s*(.*)$/)
  return {
    name: match?.[1] || "Error",
    message: match?.[2] || first || "Unknown error",
  }
}

async function responseJson(response: Response) {
  try {
    return (await response.json()) as { ok?: boolean; reportId?: string; error?: string }
  } catch {
    return undefined
  }
}

export function buildErrorReportPayload(input: {
  errorText: string
  version?: string
  channel?: string
  platform?: string
  os?: string
  email?: string
  message?: string
  diagnostics: string[]
  diagnosticsTruncated?: boolean
  diagnosticsUnavailable?: boolean
  sentryEventId?: string
}) {
  const parsed = parseErrorText(input.errorText)
  return {
    source: "openanalyst-desktop",
    kind: "error-page",
    email: input.email?.trim() || undefined,
    message: input.message?.trim() || undefined,
    app: {
      version: input.version,
      channel: input.channel,
      platform: input.platform,
      os: input.os,
    },
    error: {
      name: parsed.name,
      message: parsed.message,
      stack: input.errorText,
    },
    diagnostics: {
      entries: input.diagnostics,
      truncated: Boolean(input.diagnosticsTruncated),
      unavailable: input.diagnosticsUnavailable || undefined,
    },
    sentryEventId: input.sentryEventId,
    reportFingerprint: `${parsed.name}:${parsed.message}`.slice(0, 200),
  } satisfies ErrorReportPayload
}

export async function submitErrorReport(url: string, payload: ErrorReportPayload, fetchImpl: ErrorReportFetch = fetch) {
  const response = await fetchImpl(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  })
  const body = await responseJson(response)
  if (!response.ok || !body?.ok || !body.reportId) {
    throw new Error(body?.error ?? `Report failed with status ${response.status}`)
  }
  return { ok: true as const, reportId: body.reportId }
}
